"use client";

import Link from "next/link";
import Image from "next/image";
import { useRouter, useSearchParams } from "next/navigation";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, Mail, Lock, ArrowRight, Eye, EyeOff, User } from "lucide-react";
import { Suspense, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/toast";
import { useAuthStore } from "@/store";
import { login, register as registerApi, saveToken } from "@/lib/api";
import { useHomeHref } from "@/hooks/useHomeHref";

const loginSchema = z.object({
  email: z.string().min(1, "Informe o e-mail").email("E-mail inválido"),
  password: z.string().min(1, "Informe a senha"),
});

const signupSchema = z.object({
  name: z.string().min(2, "Nome muito curto").max(120),
  email: z.string().min(1, "Informe o e-mail").email("E-mail inválido"),
  password: z.string().min(8, "A senha precisa de pelo menos 8 caracteres").max(128),
  acceptTerms: z.literal(true, {
    message: "Você precisa aceitar os termos.",
  }),
});

type LoginData = z.infer<typeof loginSchema>;
type SignupData = z.infer<typeof signupSchema>;
type Mode = "signin" | "signup";

function LoginPageInner() {
  const router = useRouter();
  const params = useSearchParams();
  const initialMode: Mode = params.get("mode") === "signup" ? "signup" : "signin";
  const nextPath = params.get("next") || "/dashboard";

  const { setUser, setToken } = useAuthStore();
  const { error: toastError, success: toastSuccess } = useToast();
  const [mode, setMode] = useState<Mode>(initialMode);
  const [showPassword, setShowPassword] = useState(false);
  const homeHref = useHomeHref();

  const signinForm = useForm<LoginData>({ resolver: zodResolver(loginSchema) });
  const signupForm = useForm<SignupData>({
    resolver: zodResolver(signupSchema),
    defaultValues: { acceptTerms: false as unknown as true },
  });

  const onSignin = async (data: LoginData) => {
    const res = await login(data);
    if (!res.success) {
      toastError("Acesso negado", res.error.message);
      return;
    }
    setUser(res.data.user);
    setToken(res.data.accessToken);
    saveToken(res.data.accessToken);
    router.push(nextPath);
  };

  const onSignup = async (data: SignupData) => {
    const res = await registerApi(data);
    if (!res.success) {
      toastError("Não foi possível criar a conta", res.error.message);
      return;
    }
    setUser(res.data.user);
    setToken(res.data.accessToken);
    saveToken(res.data.accessToken);
    toastSuccess("Conta criada!", "Bem-vindo à ILovePage.");
    router.push(nextPath);
  };

  const isSubmitting =
    mode === "signin" ? signinForm.formState.isSubmitting : signupForm.formState.isSubmitting;

  return (
    <div className="min-h-[calc(100vh-64px)] flex flex-col lg:flex-row">
      {/* Left panel — decorativo */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-[#E11D48] to-[#881337] flex-col items-center justify-center p-12 text-white text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
        >
          <Heart className="h-20 w-20 fill-white mb-8 mx-auto opacity-90" />
          <h2 className="text-4xl font-black leading-tight mb-4">
            {mode === "signin" ? "Bem-vindo de volta!" : "Comece sua ILovePage"}
          </h2>
          <p className="text-rose-100 text-lg max-w-sm">
            {mode === "signin"
              ? "Acesse sua conta e gerencie suas ILovePages. Suas histórias de amor te esperam."
              : "Crie uma conta em segundos e tenha acesso a todas as suas ILovePages em um só lugar."}
          </p>
          <div className="mt-12 flex flex-col gap-3 text-left max-w-xs mx-auto">
            {[
              "Edite suas ILovePages a qualquer hora",
              "Veja quantas pessoas visualizaram",
              "Crie novas surpresas em minutos",
            ].map((text, i) => (
              <div key={i} className="flex items-center gap-3 text-sm text-rose-100">
                <div className="h-5 w-5 rounded-full bg-white/20 flex items-center justify-center shrink-0">
                  <span className="text-white text-xs font-bold">{i + 1}</span>
                </div>
                {text}
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Right panel — formulário */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-12 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-sm"
        >
          <Link
            href={homeHref}
            aria-label="ILovePage — início"
            className="inline-flex mb-8 lg:hidden"
          >
            <Image
              src="/brand/logo-horizontal.png"
              alt="ILovePage"
              width={2172}
              height={724}
              priority
              className="h-12 w-auto"
            />
          </Link>

          {/* Toggle pills */}
          <div className="grid grid-cols-2 bg-rose-50 p-1 rounded-xl mb-8 text-sm font-semibold">
            <button
              type="button"
              onClick={() => setMode("signin")}
              className={`py-2 rounded-lg transition ${
                mode === "signin"
                  ? "bg-white text-[#E11D48] shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Entrar
            </button>
            <button
              type="button"
              onClick={() => setMode("signup")}
              className={`py-2 rounded-lg transition ${
                mode === "signup"
                  ? "bg-white text-[#E11D48] shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Criar conta
            </button>
          </div>

          <h1 className="text-3xl font-black text-[#1F2937] mb-2">
            {mode === "signin" ? "Entrar" : "Criar conta"}
          </h1>
          <p className="text-gray-500 text-sm mb-8">
            {mode === "signin" ? (
              <>
                Não tem conta?{" "}
                <button
                  type="button"
                  onClick={() => setMode("signup")}
                  className="text-[#E11D48] font-semibold hover:underline"
                >
                  Crie grátis agora
                </button>
              </>
            ) : (
              <>
                Já tem conta?{" "}
                <button
                  type="button"
                  onClick={() => setMode("signin")}
                  className="text-[#E11D48] font-semibold hover:underline"
                >
                  Entrar
                </button>
              </>
            )}
          </p>

          <AnimatePresence mode="wait">
            {mode === "signin" ? (
              <motion.form
                key="signin"
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 8 }}
                transition={{ duration: 0.2 }}
                onSubmit={signinForm.handleSubmit(onSignin)}
                className="flex flex-col gap-5"
              >
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="email">E-mail</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="seu@email.com.br"
                      className="pl-10"
                      error={signinForm.formState.errors.email?.message}
                      {...signinForm.register("email")}
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Senha</Label>
                    <Link
                      href="/esqueci-senha"
                      className="text-xs text-[#E11D48] hover:underline"
                    >
                      Esqueceu a senha?
                    </Link>
                  </div>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Sua senha"
                      className="pl-10 pr-10"
                      error={signinForm.formState.errors.password?.message}
                      {...signinForm.register("password")}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                <Button type="submit" size="lg" loading={isSubmitting} className="mt-2">
                  {isSubmitting ? "Entrando..." : "Entrar"}
                  {!isSubmitting && <ArrowRight className="h-4 w-4" />}
                </Button>
              </motion.form>
            ) : (
              <motion.form
                key="signup"
                initial={{ opacity: 0, x: 8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -8 }}
                transition={{ duration: 0.2 }}
                onSubmit={signupForm.handleSubmit(onSignup)}
                className="flex flex-col gap-5"
              >
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="name">Nome</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="name"
                      type="text"
                      placeholder="Seu nome"
                      className="pl-10"
                      error={signupForm.formState.errors.name?.message}
                      {...signupForm.register("name")}
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="signup-email">E-mail</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder="seu@email.com.br"
                      className="pl-10"
                      error={signupForm.formState.errors.email?.message}
                      {...signupForm.register("email")}
                    />
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="signup-password">Senha</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="signup-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Mínimo 8 caracteres"
                      className="pl-10 pr-10"
                      error={signupForm.formState.errors.password?.message}
                      {...signupForm.register("password")}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                <label className="flex items-start gap-2 text-xs text-gray-600 cursor-pointer">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[#E11D48] focus:ring-[#E11D48]"
                    {...signupForm.register("acceptTerms")}
                  />
                  <span>
                    Aceito os{" "}
                    <Link href="/termos" className="text-[#E11D48] hover:underline">
                      termos de uso
                    </Link>{" "}
                    e a{" "}
                    <Link href="/privacidade" className="text-[#E11D48] hover:underline">
                      política de privacidade
                    </Link>
                    .
                  </span>
                </label>
                {signupForm.formState.errors.acceptTerms?.message && (
                  <p className="text-xs text-red-600 -mt-3">
                    {signupForm.formState.errors.acceptTerms.message}
                  </p>
                )}

                <Button type="submit" size="lg" loading={isSubmitting} className="mt-2">
                  {isSubmitting ? "Criando..." : "Criar conta"}
                  {!isSubmitting && <ArrowRight className="h-4 w-4" />}
                </Button>
              </motion.form>
            )}
          </AnimatePresence>

          <p className="text-xs text-gray-400 text-center mt-8 flex items-center justify-center gap-1.5">
            <Heart className="h-3 w-3 fill-[#E11D48] text-[#E11D48]" />
            Suas histórias de amor estão seguras com a gente.
          </p>
        </motion.div>
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense>
      <LoginPageInner />
    </Suspense>
  );
}
